import React, { useState, useEffect, useRef } from "react";
import {
	Card, Progress
} from "reactstrap";
import { registerMediaStreamSource, registerAudioContext, registerAnalyser, registerAnimationFrame } from "util/microphonePermissionManager";

export default function StepThree({
	micSensitivity, setMicSensitivity, selectedMicrophone
}) {
	const [micLevel, setMicLevel] = useState(0);
	const analyserRef = useRef(null);
	const audioContextRef = useRef(null);
	const sourceRef = useRef(null); // ✅ Store MediaStreamSource
	const streamRef = useRef(null); // ✅ Store stream
	const dataArrayRef = useRef(null);
	const animationFrameRef = useRef(null);

	useEffect(() => {
		if (selectedMicrophone !== "0") {
			microphoneLevelMonitor();
		}

		return () => {
			// Cleanup on unmount
			if (animationFrameRef.current) {
				cancelAnimationFrame(animationFrameRef.current);
				animationFrameRef.current = null;
			}
			if (sourceRef.current) {
				sourceRef.current.disconnect();
				sourceRef.current = null;
			}
			if (analyserRef.current) {
				analyserRef.current.disconnect();
				analyserRef.current = null;
			}
			if (streamRef.current) {
				streamRef.current.getTracks().forEach(track => track.stop());
				streamRef.current = null;
			}
			if (audioContextRef.current?.state !== 'closed') {
				audioContextRef.current?.close().then(() => {
					audioContextRef.current = null;
				}).catch(err => console.warn('AudioContext close error:', err));
			}
		};
	}, [selectedMicrophone]);

	const microphoneLevelMonitor = async () => {
		// Cleanup previous instances
		if (animationFrameRef.current) {
			cancelAnimationFrame(animationFrameRef.current);
			animationFrameRef.current = null;
		}
		if (sourceRef.current) {
			sourceRef.current.disconnect();
			sourceRef.current = null;
		}
		if (analyserRef.current) {
			analyserRef.current.disconnect();
			analyserRef.current = null;
		}
		if (streamRef.current) {
			streamRef.current.getTracks().forEach(track => track.stop());
			streamRef.current = null;
		}
		if (audioContextRef.current?.state !== 'closed') {
			await audioContextRef.current.close();
			audioContextRef.current = null;
		}

		// Create new session
		const stream = await navigator.mediaDevices.getUserMedia({
			audio: { deviceId: selectedMicrophone || undefined },
		});
		streamRef.current = stream;
		
		audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
		registerAudioContext(audioContextRef.current);
		
		const source = audioContextRef.current.createMediaStreamSource(stream);
		sourceRef.current = source;
		registerMediaStreamSource(source);

		analyserRef.current = audioContextRef.current.createAnalyser();
    	analyserRef.current.fftSize = 256;
		registerAnalyser(analyserRef.current);

    	const bufferLength = analyserRef.current.frequencyBinCount;
		dataArrayRef.current = new Uint8Array(bufferLength);

		source.connect(analyserRef.current);

		const checkVolume = () => {
			if (!analyserRef.current) return;
			analyserRef.current.getByteFrequencyData(dataArrayRef.current);
			const average = dataArrayRef.current.reduce((sum, val) => sum + val, 0) / bufferLength;
			setMicLevel(Math.min(100, Math.floor((average / 255) * 100)));
			animationFrameRef.current = requestAnimationFrame(checkVolume);
			registerAnimationFrame(animationFrameRef.current);
		};
		checkVolume();
	};

	return (
		<Card className="p-4 mb-3 visibility-visible">
			<div className="title mb-4">
				<span className="me-2 step-number">3</span>Test Voice Recording
			</div>

			<div className="mb-3 gray-text">
				<div className="fw-semibold mb-3">INPUT VOICE LEVEL</div>
				<div className="mb-2">Adjust Sensitivity level to have your voice appear predominantly in the green zone</div>
				<div className="mic-level mb-3">
					<div className="progress mb-1">
						<div
							className={`progress-bar ${
								micLevel < 40 ? "bg-danger"
								: micLevel < 70 ? "bg-warning"
								: "bg-success"
							}`}
							style={{
								width: `${micLevel}%`,
								transition: "width 0.1s",
							}}
						></div>
					</div>
					<div className="level-labels text-dark fs-6 clearfix">
						<span className="float-start">Low</span>
						<span className="float-end">High</span>
					</div>
				</div>
				<div className="adjust-mic mb-2">
					Adjust Microphone Sensitivity
				</div>
				<div className="adjust-mic mb-2">
					<input
						className="w-100"
						type="range"
						min={0}
						max={100}
						value={micSensitivity}
						onChange={(e) => setMicSensitivity(parseInt(e.target.value))}
					/>
				</div>
			</div>
		</Card>
	);
};